import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { submitEnquiry } from "@/lib/api";
import { useCaptcha } from "@/lib/useCaptcha";

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const initialForm = { name: "", email: "", phone: "", message: "", website: "" };

const EnquiryModal = ({ open, onClose, mode = "contact", defaultMessage = "" }) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const getCaptchaToken = useCaptcha();

  useEffect(() => {
    if (open) {
      setForm({ ...initialForm, message: defaultMessage || "" });
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, defaultMessage]);

  const isAssessment = mode === "site_assessment";
  const accent = isAssessment ? "var(--accent-crimson-hover)" : "var(--accent-blue)";
  const accentSolid = isAssessment ? "var(--accent-crimson)" : "var(--accent-blue)";
  const title = isAssessment ? "Request a site assessment" : "Contact TAFRA Energy";
  const subtitle = isAssessment
    ? "Share a few details about your site and our team will reach out to schedule an assessment."
    : "Send us a message — we typically respond within one business day.";
  const placeholder = isAssessment
    ? "Tell us about your project: site type, location, current power setup, and timeline…"
    : "How can we help?";

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!isValidEmail(form.email)) return toast.error("Please enter a valid email.");
    if (!form.message.trim()) return toast.error("Please add a short message.");

    try {
      setLoading(true);
      const token = await getCaptchaToken("enquiry");
      await submitEnquiry({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
        enquiry_type: isAssessment ? "site_assessment" : "contact",
        captcha_token: token,
        website: form.website,
      });
      toast.success(
        isAssessment
          ? "Assessment request received. We'll be in touch shortly."
          : "Message sent. We'll reply within one business day."
      );
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    color: "var(--text)",
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
          data-testid="enquiry-modal"
        >
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="modal-backdrop"
            className="absolute inset-0 backdrop-blur-md"
            style={{ background: "rgba(0,0,0,0.6)" }}
          />
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
            className="relative w-full sm:max-w-lg btn-sharp shadow-[0_30px_80px_rgba(0,0,0,0.45)]"
            style={{
              background: "var(--surface-elev)",
              border: "1px solid var(--border)",
            }}
          >
            <span
              className="absolute -top-px left-10 right-10 h-px"
              style={{
                background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
              }}
            />

            <div className="flex items-start justify-between p-7 pb-3">
              <div>
                <span
                  className="font-display uppercase tracking-[0.3em] text-[0.7rem] font-semibold"
                  style={{ color: accent }}
                >
                  {isAssessment ? "Site Assessment" : "Contact"}
                </span>
                <h3
                  className="mt-3 font-display text-2xl font-medium"
                  data-testid="modal-title"
                  style={{ color: "var(--text)" }}
                >
                  {title}
                </h3>
                <p
                  className="mt-2 text-sm max-w-md"
                  style={{ color: "var(--text-muted)" }}
                >
                  {subtitle}
                </p>
              </div>
              <button
                onClick={onClose}
                data-testid="modal-close-btn"
                className="w-9 h-9 flex items-center justify-center btn-sharp transition-colors"
                style={{ border: "1px solid var(--border)", color: "var(--text)" }}
                aria-label="Close modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form
              onSubmit={onSubmit}
              className="px-7 pb-7 pt-3 space-y-4"
              data-testid="enquiry-form"
            >
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={onChange("website")}
                data-testid="enquiry-honeypot"
                aria-hidden="true"
                className="absolute opacity-0 -z-10 pointer-events-none w-0 h-0"
                name="website"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  label="Full name"
                  testid="enquiry-name-input"
                  value={form.name}
                  onChange={onChange("name")}
                  required
                  placeholder="Your name"
                  inputStyle={inputStyle}
                />
                <FormField
                  label="Email"
                  type="email"
                  testid="enquiry-email-input"
                  value={form.email}
                  onChange={onChange("email")}
                  required
                  placeholder="you@company.com"
                  inputStyle={inputStyle}
                />
              </div>

              <FormField
                label="Phone (optional)"
                type="tel"
                testid="enquiry-phone-input"
                value={form.phone}
                onChange={onChange("phone")}
                placeholder="+254 ..."
                inputStyle={inputStyle}
              />

              <div>
                <label
                  className="block text-[11px] tracking-[0.2em] uppercase mb-2"
                  style={{ color: "var(--text-faint)" }}
                >
                  {isAssessment ? "Project details" : "Message"}
                </label>
                <textarea
                  value={form.message}
                  onChange={onChange("message")}
                  required
                  rows={5}
                  data-testid="enquiry-message-input"
                  className="w-full btn-sharp px-4 py-3 resize-none focus:outline-none"
                  style={inputStyle}
                  placeholder={placeholder}
                />
              </div>

              <p
                className="text-[11px]"
                style={{ color: "var(--text-faint)" }}
              >
                Spam-protected. Your details are never shared.
              </p>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="modal-cancel-btn"
                  className="px-5 py-3 text-sm btn-sharp transition-colors"
                  style={{
                    border: "1px solid var(--border-strong)",
                    color: "var(--text-muted)",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="enquiry-submit-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 font-semibold btn-sharp transition-all disabled:opacity-60"
                  style={{
                    background: accentSolid,
                    color: "#FFFFFF",
                    boxShadow: `0 0 18px ${accent}`,
                  }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {isAssessment ? "Submit request" : "Send message"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const FormField = ({ label, type = "text", testid, value, onChange, required, placeholder, inputStyle }) => (
  <div>
    <label
      className="block text-[11px] tracking-[0.2em] uppercase mb-2"
      style={{ color: "var(--text-faint)" }}
    >
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="w-full btn-sharp px-4 py-3 focus:outline-none"
      style={inputStyle}
      placeholder={placeholder}
    />
  </div>
);

export default EnquiryModal;
